<template>
  <div class="node-editor">
    <div class="editor-header">
      <div class="editor-header-top">
        <div class="editor-icon" :style="{ background: nodeColor + '1a', color: nodeColor }">
          <i :class="nodeIcon"></i>
        </div>
        <div class="editor-title-info">
          <input v-model="localNode.label" class="label-input" @input="emitUpdate" />
        </div>
        <button class="btn-close-editor" @click="$emit('close')">
          <i class="bx bx-x"></i>
        </button>
      </div>
    </div>

    <div class="editor-content">
      <!-- Dynamic config based on node kind -->

      <!-- REGISTRATION TRIGGER -->
      <div class="settings-section" v-if="localNode.kind === 'registration'">
        <div class="section-title">Gatilho de Inscricao</div>

        <div class="form-group">
          <label>Grupos</label>
          <multiselect
            v-model="selectedRegistrationGroups"
            :options="groups"
            :multiple="true"
            track-by="id"
            label="name"
            placeholder="Todos os grupos"
            select-label="Selecionar"
            selected-label="Selecionado"
            deselect-label="Remover"
            :max-height="200"
            open-direction="bottom"
            :close-on-select="false"
          />
          <small class="help-text">Deixe vazio para disparar em qualquer grupo</small>
        </div>

        <div class="form-group" v-if="localNode.config.group_ids && localNode.config.group_ids.length">
          <label class="d-flex align-items-center" style="gap: 8px; cursor: pointer;">
            <input
              type="checkbox"
              :checked="localNode.config.include_companions"
              @change="$set(localNode.config, 'include_companions', $event.target.checked); emitUpdate()"
            />
            Incluir acompanhantes dos grupos selecionados
          </label>
          <small class="help-text">Acompanhantes herdam a elegibilidade do grupo do participante principal</small>
        </div>
      </div>

      <!-- CHECKIN TRIGGER -->
      <div class="settings-section" v-if="localNode.kind === 'checkin'">
        <div class="section-title">Gatilho de Check-in</div>

        <div class="form-group">
          <label>Grupos</label>
          <multiselect
            v-model="selectedCheckinGroups"
            :options="groups"
            :multiple="true"
            track-by="id"
            label="name"
            placeholder="Todos os grupos"
            select-label="Selecionar"
            selected-label="Selecionado"
            deselect-label="Remover"
            :max-height="200"
            open-direction="bottom"
            :close-on-select="false"
          />
        </div>

        <div class="form-group" v-if="localNode.config.group_ids && localNode.config.group_ids.length">
          <label class="d-flex align-items-center" style="gap: 8px; cursor: pointer;">
            <input
              type="checkbox"
              :checked="localNode.config.include_companions"
              @change="$set(localNode.config, 'include_companions', $event.target.checked); emitUpdate()"
            />
            Incluir acompanhantes dos grupos selecionados
          </label>
          <small class="help-text">Acompanhantes herdam a elegibilidade do grupo do participante principal</small>
        </div>
      </div>

      <!-- TAG ADDED TRIGGER -->
      <div class="settings-section" v-if="localNode.kind === 'tag_added'">
        <div class="section-title">Gatilho de Tag</div>

        <div class="form-group">
          <label>Tag *</label>
          <select v-model="localNode.config.tag_name" class="form-control" @change="emitUpdate">
            <option value="">Selecione...</option>
            <option v-if="isOrphan(tags, localNode.config.tag_name, 'name')" :value="localNode.config.tag_name">
              {{ localNode.config.tag_name }} (fora da lista)
            </option>
            <option v-for="tag in tags" :key="tag.id" :value="tag.name">{{ tag.name }}</option>
          </select>
        </div>
      </div>

      <!-- STATUS CHANGED TRIGGER -->
      <div class="settings-section" v-if="localNode.kind === 'status_changed'">
        <div class="section-title">Gatilho de Status</div>

        <div class="form-group">
          <label>Novo status</label>
          <select v-model="localNode.config.status_id" class="form-control" @change="onStatusChangedTriggerChange">
            <option :value="null">Qualquer status</option>
            <option v-if="isOrphan(statuses, localNode.config.status_id)" :value="localNode.config.status_id">
              Status #{{ localNode.config.status_id }} (fora da lista)
            </option>
            <option v-for="s in statuses" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>

        <!-- Sub-status (quando Pendente) -->
        <div class="form-group" v-if="String(localNode.config.status_id) === '2' && Object.keys(refStatuses).length > 0">
          <label>Motivo da Pendencia</label>
          <select v-model="localNode.config.ref_status" class="form-control" @change="emitUpdate">
            <option :value="null">Qualquer motivo</option>
            <option v-for="(name, id) in refStatuses" :key="id" :value="id">{{ name }}</option>
          </select>
        </div>
      </div>

      <!-- SCHEDULED / SEGMENT TRIGGER -->
      <div class="settings-section" v-if="localNode.kind === 'scheduled'">
        <div class="section-title">Segmento Agendado</div>

        <div class="form-group">
          <label>Status do segmento <span class="text-danger">*</span></label>
          <select v-model="localNode.config.status_id" class="form-control" @change="emitUpdate">
            <option :value="null" disabled>Selecione um status</option>
            <option v-if="isOrphan(statuses, localNode.config.status_id)" :value="localNode.config.status_id">
              Status #{{ localNode.config.status_id }} (fora da lista)
            </option>
            <option v-for="s in statuses" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
          <small class="text-muted d-block mt-1">
            A rotina roda periodicamente e enrola (uma vez) quem esta neste status.
            Monte a regua com nos de "Aguardar" + "Condicao (status)" para os comunicados;
            o fluxo encerra sozinho quando o status muda.
          </small>
        </div>

        <!-- Sub-status / Motivos da Pendencia (MULTI) -->
        <div class="form-group" v-if="String(localNode.config.status_id) === '2' && Object.keys(refStatuses).length > 0">
          <label>Motivos da Pendencia <small class="text-muted">(selecione 1 ou mais)</small></label>
          <div class="ref-status-multi">
            <div class="custom-control custom-checkbox" v-for="(name, id) in refStatuses" :key="id">
              <input
                type="checkbox"
                class="custom-control-input"
                :id="'sched-refst-' + id"
                :checked="isRefStatusChecked(id)"
                @change="toggleRefStatus(id)"
              />
              <label class="custom-control-label" :for="'sched-refst-' + id">{{ name }}</label>
            </div>
          </div>
          <small class="text-muted d-block mt-1">Vazio = qualquer motivo de pendencia.</small>
        </div>
      </div>

      <!-- FORM SUBMITTED TRIGGER -->
      <div class="settings-section" v-if="localNode.kind === 'form_submitted'">
        <div class="section-title">Gatilho de Formulario</div>

        <div class="form-group">
          <label>Formulario</label>
          <select v-model="localNode.config.form_id" class="form-control" @change="emitUpdate">
            <option :value="null">Qualquer formulario</option>
            <option v-if="isOrphan(forms, localNode.config.form_id)" :value="localNode.config.form_id">
              Formulario #{{ localNode.config.form_id }} (fora da lista)
            </option>
            <option v-for="f in forms" :key="f.id" :value="f.id">{{ f.name || f.title }}</option>
          </select>
        </div>
      </div>

      <!-- WA CAMPAIGN SENT TRIGGER -->
      <div class="settings-section" v-if="localNode.kind === 'wa_campaign_sent'">
        <div class="section-title">Campanha WhatsApp Enviada</div>
        <p class="text-muted" style="font-size:12px">
          Entrada do fluxo de RSVP: o convidado e inscrito nesta jornada quando recebe o
          template de uma campanha de WhatsApp. Deixe em branco para qualquer campanha, ou
          restrinja por template.
        </p>
        <div class="form-group">
          <label>Restringir ao template</label>
          <select v-model="localNode.config.template_id" class="form-control" @change="emitUpdate">
            <option :value="null">Qualquer template</option>
            <option v-if="isOrphan(waTemplates, localNode.config.template_id)" :value="localNode.config.template_id">
              Template #{{ localNode.config.template_id }} (fora da lista)
            </option>
            <option v-for="t in waTemplates" :key="t.id" :value="t.id">{{ t.name || 'Template #' + t.id }}</option>
          </select>
        </div>
      </div>

      <!-- SEND EMAIL -->
      <div class="settings-section" v-if="localNode.kind === 'send_email'">
        <div class="section-title">Template de E-mail</div>

        <div class="form-group">
          <label>Template *</label>
          <select v-model="localNode.config.email_template_id" class="form-control" @change="emitUpdate">
            <option :value="null">Selecione...</option>
            <option v-if="isOrphan(emailTemplates, localNode.config.email_template_id)" :value="localNode.config.email_template_id">
              Template #{{ localNode.config.email_template_id }} (fora da lista)
            </option>
            <option v-for="t in emailTemplates" :key="t.id" :value="t.id">{{ t.subject || t.name || 'Template #' + t.id }}</option>
          </select>
        </div>
        <button
          v-if="localNode.config.email_template_id"
          class="btn-preview"
          @click="openEmailPreview"
        >
          <i class="bx bx-show mr-1"></i> Ver preview
        </button>
      </div>

      <!-- SEND WHATSAPP -->
      <div class="settings-section" v-if="localNode.kind === 'send_whatsapp'">
        <div class="section-title">Enviar WhatsApp</div>

        <div class="form-group">
          <label>Tipo de mensagem</label>
          <select
            class="form-control"
            :value="localNode.config.mode || 'template'"
            @change="$set(localNode.config, 'mode', $event.target.value); emitUpdate()"
          >
            <option value="template">Template (vale a qualquer hora)</option>
            <option value="text">Texto livre (dentro da janela de 24h)</option>
          </select>
        </div>

        <!-- Template -->
        <template v-if="(localNode.config.mode || 'template') === 'template'">
          <div class="form-group">
            <label>Template *</label>
            <select v-model="localNode.config.wa_template_id" class="form-control" @change="emitUpdate">
              <option :value="null">Selecione...</option>
              <option v-if="isOrphan(waTemplates, localNode.config.wa_template_id)" :value="localNode.config.wa_template_id">
                Template #{{ localNode.config.wa_template_id }} (fora da lista)
              </option>
              <option v-for="t in waTemplates" :key="t.id" :value="t.id">{{ t.name || 'Template #' + t.id }}</option>
            </select>
          </div>
          <button
            v-if="localNode.config.wa_template_id"
            class="btn-preview"
            @click="openWaPreview"
          >
            <i class="bx bx-show mr-1"></i> Ver preview
          </button>
        </template>

        <!-- Texto livre + fallback pós-24h -->
        <template v-else>
          <div class="form-group">
            <label>Texto da mensagem *</label>
            <textarea
              class="form-control"
              rows="3"
              :value="localNode.config.text"
              @input="$set(localNode.config, 'text', $event.target.value); emitUpdate()"
              placeholder="Ex: Presença confirmada! Nos vemos no evento. 🎉"
            ></textarea>
          </div>
          <div class="form-group">
            <label>Template de fallback (se a janela de 24h estiver fechada)</label>
            <select
              class="form-control"
              :value="localNode.config.fallback_wa_template_id"
              @change="$set(localNode.config, 'fallback_wa_template_id', $event.target.value ? Number($event.target.value) : null); emitUpdate()"
            >
              <option :value="null">Sem fallback (não envia fora da janela)</option>
              <option v-if="isOrphan(waTemplates, localNode.config.fallback_wa_template_id)" :value="localNode.config.fallback_wa_template_id">
                Template #{{ localNode.config.fallback_wa_template_id }} (fora da lista)
              </option>
              <option v-for="t in waTemplates" :key="t.id" :value="t.id">{{ t.name || 'Template #' + t.id }}</option>
            </select>
            <small class="text-muted d-block mt-1">
              Texto livre só é entregue com a sessão de 24h aberta. Fora dela, dispara este template.
            </small>
          </div>
        </template>
      </div>

      <!-- WA WAIT REPLY (interativo) -->
      <div class="settings-section" v-if="localNode.kind === 'wa_wait_reply'">
        <div class="section-title">WhatsApp: Esperar Clique</div>
        <p class="text-muted" style="font-size:12px">
          Pausa a jornada aguardando o convidado clicar num botao. Cada botao vira um ramo.
          O ramo "Sem resposta" dispara quando o prazo expira sem clique.
        </p>

        <div class="form-group">
          <label>Mensagem</label>
          <select v-model="localNode.config.message.mode" class="form-control" @change="emitUpdate">
            <option value="none">Nao enviar (esperar clique no template da campanha)</option>
            <option value="template">Enviar um template com botoes</option>
          </select>
        </div>

        <!-- Template de ENVIO (mode=template): os botoes sao puxados dele -->
        <div class="form-group" v-if="localNode.config.message.mode === 'template'">
          <label>Template com botoes *</label>
          <select
            class="form-control"
            :value="localNode.config.message.wa_template_id"
            @change="onWaitTemplateChange($event.target.value)"
          >
            <option :value="null">Selecione...</option>
            <option v-if="isOrphan(waTemplates, localNode.config.message.wa_template_id)" :value="localNode.config.message.wa_template_id">
              Template #{{ localNode.config.message.wa_template_id }} (fora da lista)
            </option>
            <option v-for="t in waTemplates" :key="t.id" :value="t.id">{{ t.name || 'Template #' + t.id }}</option>
          </select>
          <small class="text-muted d-block mt-1">Os botoes abaixo sao lidos automaticamente deste template.</small>
        </div>

        <!-- Template de REFERENCIA (mode=none): so para ler os botoes da campanha -->
        <div class="form-group" v-else>
          <label>Template da campanha (para ler os botoes)</label>
          <select
            class="form-control"
            :value="localNode.config.reference_template_id"
            @change="onWaitReferenceChange($event.target.value)"
          >
            <option :value="null">Selecione o template enviado na campanha...</option>
            <option v-if="isOrphan(waTemplates, localNode.config.reference_template_id)" :value="localNode.config.reference_template_id">
              Template #{{ localNode.config.reference_template_id }} (fora da lista)
            </option>
            <option v-for="t in waTemplates" :key="t.id" :value="t.id">{{ t.name || 'Template #' + t.id }}</option>
          </select>
          <small class="text-muted d-block mt-1">Nao envia nada — apenas usa os botoes deste template para montar os ramos.</small>
        </div>

        <div class="form-group">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <label class="mb-0">Botoes e ramos</label>
            <button class="btn btn-sm btn-outline-success" @click="pullWaitButtons" :disabled="!waitButtonSourceId">
              <i class="bx bx-download"></i> Puxar botoes do template
            </button>
          </div>
          <p class="text-muted" style="font-size:12px">Puxados do template selecionado. O texto deve ser identico ao botao aprovado — ajuste so se precisar.</p>
          <div v-for="(btn, i) in localNode.config.buttons" :key="i" class="inline-fields mb-2">
            <input type="text" v-model="btn.label" class="form-control" placeholder="Rotulo do botao (ex: Confirmar presenca)" @input="emitUpdate" />
            <button class="btn btn-sm btn-outline-danger" @click="removeWaitButton(i)"><i class="bx bx-trash"></i></button>
          </div>
          <button class="btn btn-sm btn-outline-primary" @click="addWaitButton">
            <i class="bx bx-plus"></i> Adicionar botao manual
          </button>
        </div>

        <div class="form-group">
          <label>Timeout (sem resposta)</label>
          <div class="inline-fields">
            <input type="number" v-model.number="localNode.config.timeout.amount" class="form-control" min="0" style="width:80px" @input="emitUpdate" />
            <select v-model="localNode.config.timeout.unit" class="form-control" @change="emitUpdate">
              <option value="minutes">Minutos</option>
              <option value="hours">Horas</option>
              <option value="days">Dias</option>
            </select>
          </div>
          <small class="text-muted d-block mt-1">0 = espera indefinidamente (sem ramo de timeout automatico).</small>
        </div>
      </div>

      <!-- WAIT TIME -->
      <div class="settings-section" v-if="localNode.kind === 'wait_time'">
        <div class="section-title">Tempo de Espera</div>

        <div class="form-group">
          <label>Tempo de espera</label>
          <div class="inline-fields">
            <input type="number" v-model.number="localNode.config.amount" class="form-control" min="1" style="width:80px" @input="emitUpdate" />
            <select v-model="localNode.config.unit" class="form-control" @change="emitUpdate">
              <option value="minutes">Minutos</option>
              <option value="hours">Horas</option>
              <option value="days">Dias</option>
              <option value="weeks">Semanas</option>
            </select>
          </div>
        </div>
        <div class="form-group toggle-group">
          <label>Pular fins de semana</label>
          <b-form-checkbox v-model="localNode.config.skip_weekends" switch @change="emitUpdate" />
        </div>
        <div class="form-group toggle-group">
          <label>Apenas horario comercial</label>
          <b-form-checkbox v-model="localNode.config.business_hours_only" switch @change="emitUpdate" />
        </div>
      </div>

      <!-- WAIT DATE -->
      <div class="settings-section" v-if="localNode.kind === 'wait_date'">
        <div class="section-title">Data Especifica</div>

        <div class="form-group">
          <label>Data e hora</label>
          <input type="datetime-local" v-model="localNode.config.date" class="form-control" @input="emitUpdate" />
        </div>
        <div class="form-group">
          <label>Fuso horario</label>
          <select v-model="localNode.config.timezone" class="form-control" @change="emitUpdate">
            <option value="America/Sao_Paulo">Brasilia (GMT-3)</option>
            <option value="America/Manaus">Manaus (GMT-4)</option>
            <option value="America/Fortaleza">Fortaleza (GMT-3)</option>
            <option value="UTC">UTC</option>
          </select>
        </div>
      </div>

      <!-- CONDITION -->
      <div class="settings-section" v-if="localNode.kind === 'condition'">
        <div class="section-title">Condicao If/Else</div>

        <!-- Step 1: Choose field -->
        <div class="form-group">
          <label>Verificar</label>
          <select v-model="localNode.config.field" class="form-control" @change="onConditionFieldChange">
            <option value="">Selecione o campo...</option>
            <!-- campo salvo que saiu do catalogo (form field desativado, chave legada) -->
            <option v-if="localNode.config.field && !conditionFieldDef" :value="localNode.config.field">
              {{ localNode.config.field }} (fora do catalogo)
            </option>
            <optgroup v-for="cat in conditionSources" :key="cat.category" :label="cat.category">
              <option v-for="f in cat.fields" :key="f.key" :value="f.key">{{ f.label }}</option>
            </optgroup>
          </select>
        </div>

        <!-- Step 2: Choose operator (dynamic per field type) -->
        <div class="form-group" v-if="localNode.config.field">
          <label>Operador</label>
          <select v-model="localNode.config.operator" class="form-control" @change="onConditionOperatorChange">
            <option v-if="isOrphanOperator" :value="localNode.config.operator">{{ localNode.config.operator }}</option>
            <option v-for="op in conditionOperators" :key="op.value" :value="op.value">{{ op.label }}</option>
          </select>
        </div>

        <!-- Step 3: Value input (dynamic per field + operator) -->
        <div class="form-group" v-if="localNode.config.field && !['is_empty', 'is_not_empty'].includes(localNode.config.operator)">
          <label>Valor</label>

          <!-- Status select -->
          <select v-if="conditionFieldDef && conditionFieldDef.valueSource === 'statuses'" v-model="localNode.config.value" class="form-control" @change="emitUpdate">
            <option value="">Selecione...</option>
            <option v-if="isOrphan(statuses, localNode.config.value)" :value="String(localNode.config.value)">
              Status #{{ localNode.config.value }} (fora da lista)
            </option>
            <option v-for="s in statuses" :key="s.id" :value="String(s.id)">{{ s.name }}</option>
          </select>

          <!-- Group select -->
          <select v-else-if="conditionFieldDef && conditionFieldDef.valueSource === 'groups'" v-model="localNode.config.value" class="form-control" @change="emitUpdate">
            <option value="">Selecione...</option>
            <option v-if="isOrphan(groups, localNode.config.value)" :value="String(localNode.config.value)">
              Grupo #{{ localNode.config.value }} (fora da lista)
            </option>
            <option v-for="g in groups" :key="g.id" :value="String(g.id)">{{ g.name }}</option>
          </select>

          <!-- Tag select -->
          <select v-else-if="conditionFieldDef && conditionFieldDef.valueSource === 'tags'" v-model="localNode.config.value" class="form-control" @change="emitUpdate">
            <option value="">Selecione...</option>
            <option v-if="isOrphan(tags, localNode.config.value, 'name')" :value="localNode.config.value">
              {{ localNode.config.value }} (fora da lista)
            </option>
            <option v-for="tag in tags" :key="tag.id" :value="tag.name">{{ tag.name }}</option>
          </select>

          <!-- Boolean select -->
          <select v-else-if="conditionFieldDef && conditionFieldDef.fieldType === 'boolean'" v-model="localNode.config.value" class="form-control" @change="emitUpdate">
            <option value="1">Sim</option>
            <option value="0">Nao</option>
          </select>

          <!-- Static options (gender, need_payment, etc.) -->
          <select v-else-if="conditionFieldDef && conditionFieldDef.fixedOptions" v-model="localNode.config.value" class="form-control" @change="emitUpdate">
            <option value="">Selecione...</option>
            <option v-for="opt in conditionFieldDef.fixedOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>

          <!-- Step reference (engagement) -->
          <select v-else-if="conditionFieldDef && conditionFieldDef.valueSource === 'step_ref'" v-model="localNode.config.node_ref_id" class="form-control" @change="emitUpdate">
            <option :value="null">Selecione o step...</option>
            <option v-for="n in getStepsByKind(conditionFieldDef.stepKind)" :key="n.id || n._tempId" :value="n.id || n._tempId">{{ n.label }}</option>
          </select>

          <!-- Number input -->
          <input v-else-if="conditionFieldDef && conditionFieldDef.fieldType === 'number'" type="number" v-model="localNode.config.value" class="form-control" placeholder="Valor" @input="emitUpdate" />

          <!-- Date input -->
          <input v-else-if="conditionFieldDef && conditionFieldDef.fieldType === 'date'" type="date" v-model="localNode.config.value" class="form-control" @input="emitUpdate" />

          <!-- Default text input -->
          <input v-else v-model="localNode.config.value" class="form-control" placeholder="Valor" @input="emitUpdate" />
        </div>

        <!-- Sub-status (quando condição status = Pendente) — MULTI motivos -->
        <div class="form-group" v-if="localNode.config.field === 'status' && String(localNode.config.value) === '2' && Object.keys(refStatuses).length > 0">
          <label>Motivos da Pendencia <small class="text-muted">(1 ou mais)</small></label>
          <div class="ref-status-multi">
            <div class="custom-control custom-checkbox" v-for="(name, id) in refStatuses" :key="id">
              <input
                type="checkbox"
                class="custom-control-input"
                :id="'cond-refst-' + id"
                :checked="isRefStatusChecked(id)"
                @change="toggleRefStatus(id)"
              />
              <label class="custom-control-label" :for="'cond-refst-' + id">{{ name }}</label>
            </div>
          </div>
          <small class="text-muted d-block mt-1">Vazio = qualquer motivo. A régua segue enquanto o motivo estiver nesta lista; para quando sair dela.</small>
        </div>

        <!-- Summary -->
        <div class="condition-summary" v-if="conditionSummary">
          <i class="bx bx-git-branch mr-1"></i>
          <span v-html="conditionSummary"></span>
        </div>

        <!-- Retry: Aguardar até verdadeiro -->
        <div class="retry-section">
          <div class="form-group toggle-group">
            <label>Aguardar ate verdadeiro</label>
            <b-form-checkbox v-model="localNode.config.retry_enabled" switch @change="emitUpdate" />
          </div>

          <template v-if="localNode.config.retry_enabled">
            <div class="form-group">
              <label>Verificar a cada</label>
              <div class="inline-fields">
                <input type="number" v-model.number="localNode.config.retry_interval_amount" class="form-control" min="1" style="width:80px" @input="emitUpdate" />
                <select v-model="localNode.config.retry_interval_unit" class="form-control" @change="emitUpdate">
                  <option value="minutes">Minutos</option>
                  <option value="hours">Horas</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label>Timeout apos</label>
              <div class="inline-fields">
                <input type="number" v-model.number="localNode.config.retry_timeout_amount" class="form-control" min="1" style="width:80px" @input="emitUpdate" />
                <select v-model="localNode.config.retry_timeout_unit" class="form-control" @change="emitUpdate">
                  <option value="hours">Horas</option>
                  <option value="days">Dias</option>
                </select>
              </div>
            </div>

            <small class="help-text">
              <i class="bx bx-info-circle mr-1"></i>
              Se nao for verdadeiro, reagenda automaticamente. Branch <b>Nao</b> so executa quando o timeout estourar.
            </small>
          </template>
        </div>

        <small class="help-text mt-2">
          <i class="bx bx-info-circle mr-1"></i>
          Cria duas ramificacoes: <b>Sim</b> (condicao verdadeira) e <b>Nao</b> (condicao falsa)
        </small>
      </div>

      <!-- AB TEST -->
      <div class="settings-section" v-if="localNode.kind === 'ab_test'">
        <div class="section-title">Teste A/B</div>

        <div class="form-group">
          <label>Variante A: {{ localNode.config.variant_a_pct }}%</label>
          <input type="range" v-model.number="localNode.config.variant_a_pct" min="10" max="90" step="5" class="form-range w-100" @input="onAbTestChange" />
        </div>
        <div class="form-group">
          <label>Variante B: {{ localNode.config.variant_b_pct }}%</label>
        </div>
      </div>

      <!-- ADD TAG / REMOVE TAG -->
      <div class="settings-section" v-if="localNode.kind === 'add_tag' || localNode.kind === 'remove_tag'">
        <div class="section-title">{{ localNode.kind === 'add_tag' ? 'Adicionar Tag' : 'Remover Tag' }}</div>

        <div class="form-group">
          <label>Tag *</label>
          <select v-model="localNode.config.tag_name" class="form-control" @change="emitUpdate">
            <option value="">Selecione...</option>
            <option v-if="isOrphan(tags, localNode.config.tag_name, 'name')" :value="localNode.config.tag_name">
              {{ localNode.config.tag_name }} (fora da lista)
            </option>
            <option v-for="tag in tags" :key="tag.id" :value="tag.name">{{ tag.name }}</option>
          </select>
        </div>
      </div>

      <!-- UPDATE STATUS -->
      <div class="settings-section" v-if="localNode.kind === 'update_status'">
        <div class="section-title">Alterar Status</div>

        <div class="form-group">
          <label>Novo Status *</label>
          <select v-model="localNode.config.field_value" class="form-control" @change="onUpdateStatusChange">
            <option value="">Selecione...</option>
            <option v-if="isOrphan(statuses, localNode.config.field_value)" :value="String(localNode.config.field_value)">
              Status #{{ localNode.config.field_value }} (fora da lista)
            </option>
            <option v-for="s in statuses" :key="s.id" :value="String(s.id)">{{ s.name }}</option>
          </select>
        </div>

        <!-- Sub-status (quando Pendente) -->
        <div class="form-group" v-if="String(localNode.config.field_value) === '2' && Object.keys(refStatuses).length > 0">
          <label>Motivo da Pendencia</label>
          <select v-model="localNode.config.ref_status" class="form-control" @change="emitUpdate">
            <option :value="null">Nenhum</option>
            <option v-for="(name, id) in refStatuses" :key="id" :value="id">{{ name }}</option>
          </select>
        </div>
      </div>

      <!-- UPDATE FIELD -->
      <div class="settings-section" v-if="localNode.kind === 'update_field'">
        <div class="section-title">Atualizar Campo</div>

        <div class="form-group">
          <label>Campo *</label>
          <select v-model="localNode.config.field_id" class="form-control" @change="onFieldChange">
            <option :value="null">Selecione...</option>
            <option v-if="isOrphan(flatFieldTypes, localNode.config.field_id)" :value="localNode.config.field_id">
              {{ localNode.config.field_name || 'Campo #' + localNode.config.field_id }} (fora da lista)
            </option>
            <optgroup v-for="cat in fieldTypesGrouped" :key="cat.category" :label="cat.category">
              <option v-for="ft in cat.types" :key="ft.id" :value="ft.id">{{ ft.label }}</option>
            </optgroup>
          </select>
          <small v-if="selectedFieldType" class="help-text">
            Tipo: {{ selectedFieldType.component || selectedFieldType.type }}
          </small>
        </div>

        <div class="form-group" v-if="localNode.config.field_id">
          <label>Novo Valor *</label>
          <!-- Field with options: show select -->
          <select
            v-if="selectedFieldType && selectedFieldType.has_options && selectedFieldType.options && selectedFieldType.options.length"
            v-model="localNode.config.field_value"
            class="form-control"
            @change="emitUpdate"
          >
            <option value="">Selecione...</option>
            <option v-for="opt in selectedFieldType.options" :key="opt.value || opt" :value="opt.value || opt">
              {{ opt.label || opt }}
            </option>
          </select>
          <!-- Checkbox/boolean field -->
          <div v-else-if="selectedFieldType && ['checkbox', 'toggle', 'switch'].includes(selectedFieldType.component)" class="toggle-group">
            <label class="mb-0">Valor</label>
            <b-form-checkbox v-model="localNode.config.field_value" switch value="1" unchecked-value="0" @change="emitUpdate" />
          </div>
          <!-- Number field -->
          <input
            v-else-if="selectedFieldType && ['number', 'currency', 'cpf', 'cnpj', 'phone'].includes(selectedFieldType.component)"
            v-model="localNode.config.field_value"
            type="number"
            class="form-control"
            placeholder="Valor"
            @input="emitUpdate"
          />
          <!-- Date field -->
          <input
            v-else-if="selectedFieldType && ['date', 'datetime'].includes(selectedFieldType.component)"
            v-model="localNode.config.field_value"
            type="date"
            class="form-control"
            @input="emitUpdate"
          />
          <!-- Textarea field -->
          <textarea
            v-else-if="selectedFieldType && ['textarea', 'long_text'].includes(selectedFieldType.component)"
            v-model="localNode.config.field_value"
            class="form-control"
            rows="3"
            placeholder="Valor"
            @input="emitUpdate"
          ></textarea>
          <!-- Default: text input -->
          <input
            v-else
            v-model="localNode.config.field_value"
            class="form-control"
            placeholder="Valor"
            @input="emitUpdate"
          />
        </div>
      </div>

      <!-- SEND FOLLOW UP ACTION -->
      <div class="settings-section" v-if="localNode.kind === 'send_follow_up_action'">
        <div class="section-title">Acao de Retorno</div>

        <div class="form-group">
          <label>Regra de Follow-up *</label>
          <select v-model="localNode.config.follow_up_rule_id" class="form-control" @change="emitUpdate">
            <option :value="null">Selecione...</option>
            <option v-if="isOrphan(followUpRules, localNode.config.follow_up_rule_id)" :value="localNode.config.follow_up_rule_id">
              Regra #{{ localNode.config.follow_up_rule_id }} (fora da lista)
            </option>
            <option v-for="r in followUpRules" :key="r.id" :value="r.id">{{ r.name }}</option>
          </select>
          <small v-if="selectedFollowUpRule" class="help-text">
            Form: {{ selectedFollowUpRule.form ? selectedFollowUpRule.form.name : '-' }}
            <template v-if="selectedFollowUpRule.token_expiration_hours">
              | Validade: {{ selectedFollowUpRule.token_expiration_hours }}h
            </template>
          </small>
          <small v-else-if="!followUpRules.length" class="help-text text-warning">
            <i class="bx bx-error-circle mr-1"></i>
            Nenhuma regra ativa encontrada. Crie em "Acoes de Retorno".
          </small>
        </div>

        <div class="form-group toggle-group">
          <label>Disparar e-mail da regra</label>
          <b-form-checkbox v-model="localNode.config.send_email" switch @change="emitUpdate" />
        </div>
        <small class="help-text">
          <i class="bx bx-info-circle mr-1"></i>
          Cria a pending action e (se marcado) envia o magic link usando o template da regra.
          Se o participante ja tem uma pending action ativa para esta regra, ela e reaproveitada.
        </small>
      </div>

      <!-- INTERNAL NOTE -->
      <div class="settings-section" v-if="localNode.kind === 'internal_note'">
        <div class="section-title">Nota Interna</div>

        <div class="form-group">
          <label>Texto da Nota *</label>
          <textarea v-model="localNode.config.note_text" class="form-control" rows="4" placeholder="Ex: Participante confirmou presenca via jornada automatica" @input="emitUpdate"></textarea>
        </div>

        <div class="merge-tags-section">
          <label class="merge-tags-label">Variaveis disponiveis</label>
          <div class="merge-tags-list">
            <span class="merge-tag" v-for="tag in mergeTags" :key="tag.tag" @click="insertMergeTag(tag.tag)" :title="tag.desc">
              {{ tag.tag }}
            </span>
          </div>
        </div>

        <div class="note-destination">
          <div class="dest-item">
            <i class="bx bx-message-square-detail"></i>
            <div>
              <span class="dest-title">Comentario no historico</span>
              <span class="dest-desc">Adicionado como comentario na timeline do participante</span>
            </div>
          </div>
          <div class="dest-item">
            <i class="bx bx-history"></i>
            <div>
              <span class="dest-title">Registro de atividade</span>
              <span class="dest-desc">Registrado como atividade da jornada com tipo e detalhes</span>
            </div>
          </div>
        </div>
      </div>

      <!-- END JOURNEY -->
      <div class="settings-section" v-if="localNode.kind === 'end_journey'">
        <div class="section-title">Encerrar Jornada</div>

        <div class="form-group">
          <label>Motivo</label>
          <select v-model="localNode.config.reason" class="form-control" @change="emitUpdate">
            <option value="completed">Concluida</option>
            <option value="goal_reached">Objetivo alcancado</option>
            <option value="timeout">Tempo esgotado</option>
            <option value="manual">Manual</option>
          </select>
        </div>
      </div>

      <!-- GO TO JOURNEY -->
      <div class="settings-section" v-if="localNode.kind === 'go_to_journey'">
        <div class="section-title">Ir Para Jornada</div>

        <div class="form-group">
          <label>Jornada destino *</label>
          <select v-model="localNode.config.target_journey_id" class="form-control" @change="emitUpdate">
            <option :value="null">Selecione...</option>
            <option v-if="isOrphan(journeysList, localNode.config.target_journey_id)" :value="localNode.config.target_journey_id">
              Jornada #{{ localNode.config.target_journey_id }} (fora da lista)
            </option>
            <option v-for="j in journeysList" :key="j.id" :value="j.id">{{ j.name }}</option>
          </select>
        </div>
      </div>

      <!-- ENGAGEMENT CHECKS -->
      <div class="settings-section" v-if="localNode.kind === 'email_engagement' || localNode.kind === 'wa_engagement'">
        <div class="section-title">Engajamento</div>

        <div class="form-group">
          <label>Tipo de engajamento</label>
          <select v-model="localNode.config.rule_type" class="form-control" @change="emitUpdate">
            <option value="opened" v-if="localNode.kind === 'email_engagement'">Abriu o email</option>
            <option value="clicked" v-if="localNode.kind === 'email_engagement'">Clicou no email</option>
            <option value="replied" v-if="localNode.kind === 'wa_engagement'">Respondeu WhatsApp</option>
            <option value="delivered" v-if="localNode.kind === 'wa_engagement'">Recebeu WhatsApp</option>
          </select>
        </div>
        <div class="form-group">
          <label>Step de referencia</label>
          <select v-model="localNode.config.node_ref_id" class="form-control" @change="emitUpdate">
            <option :value="null">Selecione...</option>
            <option v-for="n in refNodes" :key="n.id || n._tempId" :value="n.id || n._tempId">{{ n.label }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="editor-footer">
      <button class="btn-remove-node" @click="$emit('remove-node', localNode.id || localNode._tempId)">
        <i class="bx bx-trash mr-1"></i> Remover Step
      </button>
    </div>

    <!-- Template Preview Modal -->
    <b-modal v-model="showPreview" :title="previewType === 'email' ? 'Preview do E-mail' : 'Preview do WhatsApp'" size="lg" hide-footer centered>
      <div v-if="previewType === 'email'" class="preview-email" v-html="previewContent"></div>
      <div v-else class="preview-whatsapp">
        <div class="wa-bubble">{{ previewContent }}</div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { getNodeColor, getNodeIcon, getNodeDefaults } from '../nodeLibrary'
import Multiselect from 'vue-multiselect'
import { errorMessage } from '@/components/alertsMessages'

export default {
  components: { Multiselect },
  props: {
    node: { type: Object, required: true },
    emailTemplates: { type: Array, default: () => [] },
    waTemplates: { type: Array, default: () => [] },
    statuses: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    tags: { type: Array, default: () => [] },
    forms: { type: Array, default: () => [] },
    nodes: { type: Array, default: () => [] },
    journeysList: { type: Array, default: () => [] },
    fieldTypes: { type: Array, default: () => [] },
    refStatuses: { type: Object, default: () => ({}) },
    formConditionFields: { type: Array, default: () => [] },
    followUpRules: { type: Array, default: () => [] }
  },
  data () {
    return {
      localNode: this.cloneNode(this.node),
      showPreview: false,
      previewType: null,
      previewContent: '',
      mergeTags: [
        { tag: '{$nome}', desc: 'Nome do participante' },
        { tag: '{$email}', desc: 'Email do participante' },
        { tag: '{$evento}', desc: 'Nome do evento' },
        { tag: '{$status}', desc: 'Status atual' },
        { tag: '{$grupo}', desc: 'Nome do grupo' },
        { tag: '{$data}', desc: 'Data atual' }
      ]
    }
  },
  computed: {
    // Template do qual os botoes serao lidos: o de envio (mode=template) ou o de
    // referencia da campanha (mode=none).
    waitButtonSourceId () {
      if (!this.localNode || this.localNode.kind !== 'wa_wait_reply') return null
      const cfg = this.localNode.config || {}
      const mode = (cfg.message && cfg.message.mode) || 'none'
      return mode === 'template'
        ? (cfg.message && cfg.message.wa_template_id) || null
        : cfg.reference_template_id || null
    },
    /**
     * form_id alvo da jornada (Fase 3): inferido a partir do trigger atual no canvas.
     * Reativo — se o usuario trocar o form do trigger antes de salvar, a ordenacao do
     * picker reflete sem precisar de refetch.
     */
    targetFormId () {
      if (!Array.isArray(this.nodes)) return null
      const trigger = this.nodes.find(n => n && n.kind === 'form_submitted')
      if (!trigger || !trigger.config) return null
      return trigger.config.form_id || null
    },

    conditionSources () {
      // Fonte unica: catalogo unificado do backend (FilterCatalogService::getJourneyConditionFields).
      // Vem como [{ category, label, fields: [{ key, label, field_type, component, multiple, static_options, source, operators }] }].
      // Mapeamos para o shape interno do NodeEditor (categoria + fields com fieldType/valueSource/fixedOptions/operators).
      const sources = []
      const catalog = Array.isArray(this.formConditionFields) ? this.formConditionFields : []
      const targetCategory = this.targetFormId ? `form_${this.targetFormId}` : null

      catalog.forEach(category => {
        if (!category || !Array.isArray(category.fields) || category.fields.length === 0) return

        const fields = category.fields.map(f => this.mapCatalogFieldToSource(f, category)).filter(Boolean)
        if (fields.length === 0) return

        const isPrimary = (targetCategory && category.category === targetCategory) || category.is_primary === true
        const baseLabel = category.label || category.category || 'Campos'
        sources.push({
          category: isPrimary ? `★ ${baseLabel} (form atual)` : baseLabel,
          rawCategory: category.category,
          isPrimary,
          fields
        })
      })

      // Engajamento - referencia a steps do proprio fluxo (nao vem do catalogo do backend
      // porque depende do contexto local da jornada, nao do catalogo de campos do evento).
      sources.push({
        category: 'Engajamento',
        rawCategory: 'engagement',
        isPrimary: false,
        fields: [
          { key: 'email_opened', label: 'Abriu e-mail', fieldType: 'step_ref', valueSource: 'step_ref', stepKind: 'send_email' },
          { key: 'email_clicked', label: 'Clicou no e-mail', fieldType: 'step_ref', valueSource: 'step_ref', stepKind: 'send_email' },
          { key: 'wa_replied', label: 'Respondeu WhatsApp', fieldType: 'step_ref', valueSource: 'step_ref', stepKind: 'send_whatsapp' },
          { key: 'wa_delivered', label: 'Recebeu WhatsApp', fieldType: 'step_ref', valueSource: 'step_ref', stepKind: 'send_whatsapp' }
        ]
      })

      // Reordena: primary primeiro, depois standard (Campos Padrao do Participante), depois o resto.
      // Preserva ordem relativa dentro de cada bucket.
      return sources.slice().sort((a, b) => {
        const rank = c => {
          if (c.isPrimary) return 0
          if (c.rawCategory === 'standard') return 1
          if (c.rawCategory === 'engagement') return 3
          return 2
        }
        return rank(a) - rank(b)
      })
    },
    selectedRegistrationGroups: {
      get () {
        // includes() e estrito: um group_ids vindo com string (import, IA, legado)
        // zerava o multiselect inteiro mesmo com os grupos certos configurados.
        const ids = (this.localNode.config.group_ids || []).map(String)
        return this.groups.filter(g => ids.includes(String(g.id)))
      },
      set (val) {
        this.$set(this.localNode.config, 'group_ids', val.map(g => g.id))
        this.emitUpdate()
      }
    },
    selectedCheckinGroups: {
      get () {
        const ids = (this.localNode.config.group_ids || []).map(String)
        return this.groups.filter(g => ids.includes(String(g.id)))
      },
      set (val) {
        this.$set(this.localNode.config, 'group_ids', val.map(g => g.id))
        this.emitUpdate()
      }
    },
    nodeId () {
      return this.node ? (this.node.id || this.node._tempId) : null
    },
    nodeColor () { return getNodeColor(this.localNode.kind) },
    nodeIcon () { return getNodeIcon(this.localNode.kind) },
    refNodes () {
      const kinds = this.localNode.kind === 'email_engagement'
        ? ['send_email']
        : ['send_whatsapp']
      return this.nodes.filter(n => kinds.includes(n.kind))
    },
    fieldTypesGrouped () {
      return this.fieldTypes || []
    },
    // Lista plana dos tipos de campo, para checar orfao sem repetir o loop.
    flatFieldTypes () {
      return this.fieldTypesGrouped.reduce((acc, cat) => acc.concat(cat.types || []), [])
    },
    // Comparacoes por String(): a mesma chave e gravada como string pela UI
    // (String(s.id)) e como int pelo compositor por IA.
    selectedFieldType () {
      if (!this.localNode.config.field_id) return null
      return this.flatFieldTypes.find(ft => String(ft.id) === String(this.localNode.config.field_id)) || null
    },
    selectedEmailTemplate () {
      if (!this.localNode.config.email_template_id) return null
      return this.emailTemplates.find(t => String(t.id) === String(this.localNode.config.email_template_id))
    },
    selectedFollowUpRule () {
      const id = this.localNode.config.follow_up_rule_id
      if (!id) return null
      return (this.followUpRules || []).find(r => String(r.id) === String(id)) || null
    },
    selectedWaTemplate () {
      if (!this.localNode.config.wa_template_id) return null
      return this.waTemplates.find(t => String(t.id) === String(this.localNode.config.wa_template_id))
    },
    // Operador salvo que nao consta na lista do tipo de campo atual (ex.: o campo
    // saiu do catalogo). Sem a option orfa o select aparece vazio.
    isOrphanOperator () {
      const op = this.localNode.config.operator
      if (!op) return false
      return !this.conditionOperators.some(o => String(o.value) === String(op))
    },
    conditionFieldDef () {
      if (!this.localNode.config.field) return null
      for (const cat of this.conditionSources) {
        const found = cat.fields.find(f => f.key === this.localNode.config.field)
        if (found) return found
      }
      return null
    },
    conditionOperators () {
      const def = this.conditionFieldDef
      if (!def) return []
      // Se o catalogo do backend ja entregou a lista de operadores validos pro tipo, usa direto.
      if (Array.isArray(def.operators) && def.operators.length > 0) {
        return def.operators
      }
      switch (def.fieldType) {
        case 'text':
          return [
            { value: 'equals', label: 'Igual a' },
            { value: 'not_equals', label: 'Diferente de' },
            { value: 'contains', label: 'Contem' },
            { value: 'not_contains', label: 'Nao contem' },
            { value: 'starts_with', label: 'Comeca com' },
            { value: 'ends_with', label: 'Termina com' },
            { value: 'is_empty', label: 'Esta vazio' },
            { value: 'is_not_empty', label: 'Nao esta vazio' }
          ]
        case 'select':
          return [
            { value: 'equals', label: 'Igual a' },
            { value: 'not_equals', label: 'Diferente de' }
          ]
        case 'boolean':
          return [
            { value: 'equals', label: 'Igual a' }
          ]
        case 'number':
          return [
            { value: 'equals', label: 'Igual a' },
            { value: 'not_equals', label: 'Diferente de' },
            { value: 'greater_than', label: 'Maior que' },
            { value: 'less_than', label: 'Menor que' },
            { value: 'is_empty', label: 'Esta vazio' },
            { value: 'is_not_empty', label: 'Nao esta vazio' }
          ]
        case 'date':
          return [
            { value: 'before', label: 'Antes de' },
            { value: 'after', label: 'Depois de' },
            { value: 'equals', label: 'Igual a' }
          ]
        case 'step_ref':
          return [
            { value: 'is_true', label: 'Sim' },
            { value: 'is_false', label: 'Nao' }
          ]
        default:
          return [
            { value: 'equals', label: 'Igual a' },
            { value: 'not_equals', label: 'Diferente de' }
          ]
      }
    },
    conditionSummary () {
      const field = this.conditionFieldDef
      const op = this.localNode.config.operator
      const val = this.localNode.config.value
      if (!field || !op) return ''
      const opLabel = (this.conditionOperators.find(o => o.value === op) || {}).label || op
      if (['is_empty', 'is_not_empty'].includes(op)) {
        return `Se <b>${field.label}</b> ${opLabel.toLowerCase()}`
      }
      if (field.fieldType === 'step_ref') {
        return `Se <b>${field.label}</b>: ${opLabel}`
      }
      if (!val && val !== 0) return ''
      return `Se <b>${field.label}</b> ${opLabel.toLowerCase()} <b>${val}</b>`
    }
  },
  watch: {
    nodeId () {
      this.localNode = this.cloneNode(this.node)
    }
  },
  beforeDestroy () {
    this.$emit('update-node', JSON.parse(JSON.stringify(this.localNode)))
  },
  methods: {
    /**
     * Converte um field do catalogo unificado para o shape que o template do
     * editor consome (fieldType + valueSource + fixedOptions).
     */
    mapCatalogFieldToSource (f, category) {
      if (!f || !f.key) return null
      const out = {
        key: f.key,
        label: f.label || f.key,
        fieldType: f.field_type || f.fieldType || 'text',
        operators: Array.isArray(f.operators) ? f.operators : undefined
      }

      // Source dinamico (statuses/groups/tags) - vem do `source` do catalogo no formato 'vuex:...'.
      if (typeof f.source === 'string') {
        if (f.source.includes('status')) out.valueSource = 'statuses'
        else if (f.source.includes('group')) out.valueSource = 'groups'
        else if (f.source.includes('tag')) out.valueSource = 'tags'
      }
      // Heuristica por key: cobre standard fields que nao mandam `source` explicitamente
      if (!out.valueSource) {
        if (f.key === 'status') out.valueSource = 'statuses'
        else if (f.key === 'group_id') out.valueSource = 'groups'
        else if (f.key === 'tags' || f.key === 'has_tag' || f.key === 'not_has_tag') out.valueSource = 'tags'
      }

      // Options fixas (vindas de static_options do catalogo) - sempre { value, label }
      if (Array.isArray(f.static_options) && f.static_options.length > 0) {
        out.fixedOptions = f.static_options.map(opt => {
          if (opt && typeof opt === 'object') {
            const v = opt.value !== undefined ? opt.value : (opt.label || '')
            const l = opt.label !== undefined ? opt.label : v
            return { value: String(v), label: String(l) }
          }
          return { value: String(opt), label: String(opt) }
        }).filter(o => o.value !== '')
      }

      return out
    },

    cloneNode (node) {
      const clone = JSON.parse(JSON.stringify(node))
      // PHP/Laravel retorna [] (array) quando config esta vazio - converter para {}
      if (!clone.config || Array.isArray(clone.config)) clone.config = {}
      // Merge default config keys from nodeLibrary for Vue 2 reactivity
      const defaults = getNodeDefaults(clone.kind)
      if (defaults && defaults.config) {
        clone.config = this.mergeDefaults(defaults.config, clone.config)
      }
      // Migrar legado group_id -> group_ids
      if ((clone.kind === 'registration' || clone.kind === 'checkin') && !clone.config.group_ids) {
        clone.config.group_ids = clone.config.group_id ? [clone.config.group_id] : []
      }
      return clone
    },
    isPlainObject (v) {
      return v !== null && typeof v === 'object' && !Array.isArray(v)
    },
    /**
     * Aplica os defaults do nodeLibrary sobre a config salva, RECURSIVAMENTE.
     * O merge antigo so cobria o primeiro nivel: um wa_wait_reply salvo com
     * `message` presente mas sem `message.mode` ficava com undefined, o select
     * renderizava vazio e o v-if derrubava o campo do template junto.
     *
     * O valor salvo sempre vence, com uma excecao: null onde o default e string
     * vazia. O middleware ConvertEmptyStringsToNull do backend e recursivo e
     * converte '' -> null dentro do proprio config.
     */
    mergeDefaults (defaults, saved) {
      const out = { ...saved }
      Object.keys(defaults).forEach(key => {
        const def = defaults[key]
        const val = out[key]
        if (!(key in out) || val === undefined) {
          out[key] = def
        } else if (val === null && def === '') {
          out[key] = def
        } else if (this.isPlainObject(def) && this.isPlainObject(val)) {
          out[key] = this.mergeDefaults(def, val)
        } else if (this.isPlainObject(def) && val === null) {
          out[key] = def
        }
      })
      return out
    },
    /**
     * true quando ha um valor configurado que NAO existe na lista carregada:
     * lista paginada que cortou o item, endpoint que falhou, ou registro
     * removido. Nesses casos o select ganha uma option "orfa" com o proprio
     * valor, em vez de renderizar em branco escondendo a configuracao do no.
     */
    isOrphan (list, value, prop) {
      if (value === null || value === undefined || value === '') return false
      const key = prop || 'id'
      return !(list || []).some(item => item && String(item[key]) === String(value))
    },
    emitUpdate () {
      this.$emit('update-node', JSON.parse(JSON.stringify(this.localNode)))
    },
    // Multi-select dos motivos de pendência (gatilho Segmento Agendado)
    isRefStatusChecked (id) {
      const list = this.localNode.config.ref_statuses || []
      return list.map(String).includes(String(id))
    },
    toggleRefStatus (id) {
      if (!Array.isArray(this.localNode.config.ref_statuses)) {
        this.$set(this.localNode.config, 'ref_statuses', [])
      }
      const list = this.localNode.config.ref_statuses
      const idx = list.findIndex(x => String(x) === String(id))
      if (idx >= 0) list.splice(idx, 1)
      else list.push(id)
      this.emitUpdate()
    },
    onAbTestChange () {
      this.localNode.config.variant_b_pct = 100 - this.localNode.config.variant_a_pct
      this.emitUpdate()
    },
    // Botoes do node interativo (wa_wait_reply). Cada botao ganha uma chave de ramo
    // estavel (b1, b2, ...) usada como branch_type dos nos filhos daquele ramo.
    addWaitButton () {
      if (!Array.isArray(this.localNode.config.buttons)) {
        this.$set(this.localNode.config, 'buttons', [])
      }
      const used = this.localNode.config.buttons.map(b => b.branch)
      let n = 1
      while (used.includes('b' + n)) n++
      this.localNode.config.buttons.push({ label: '', branch: 'b' + n })
      this.emitUpdate()
    },
    // Extrai os botoes de resposta rapida (QUICK_REPLY) de um template do waTemplates.
    templateQuickReplies (templateId) {
      const t = this.waTemplates.find(x => String(x.id) === String(templateId))
      if (!t) return []
      let comps = t.components
      if (typeof comps === 'string') {
        try { comps = JSON.parse(comps) } catch (e) { comps = [] }
      }
      const out = []
      ;(comps || []).forEach(c => {
        if ((c.type || '').toUpperCase() === 'BUTTONS') {
          ;(c.buttons || []).forEach((b) => {
            if ((b.type || '').toUpperCase() === 'QUICK_REPLY' && b.text) {
              out.push(b.text)
            }
          })
        }
      })
      return out
    },
    // Puxa os botoes do template selecionado (envio ou referencia) para os ramos.
    // Preserva a chave de ramo por rotulo, para nao orfanar nos filhos ja montados.
    pullWaitButtons () {
      const labels = this.templateQuickReplies(this.waitButtonSourceId)
      if (!labels.length) {
        errorMessage('Este template nao tem botoes de resposta rapida (quick reply).')
        return
      }
      const existing = Array.isArray(this.localNode.config.buttons) ? this.localNode.config.buttons : []
      const used = []
      const next = labels.map(label => {
        const match = existing.find(b => (b.label || '').trim().toLowerCase() === label.trim().toLowerCase())
        if (match && match.branch) {
          used.push(match.branch)
          return { label, branch: match.branch }
        }
        let n = 1
        while (used.includes('b' + n)) n++
        used.push('b' + n)
        return { label, branch: 'b' + n }
      })
      this.$set(this.localNode.config, 'buttons', next)
      this.emitUpdate()
    },
    onWaitTemplateChange (val) {
      const id = val ? Number(val) : null
      this.$set(this.localNode.config.message, 'wa_template_id', id)
      this.$nextTick(() => this.pullWaitButtons())
    },
    onWaitReferenceChange (val) {
      const id = val ? Number(val) : null
      this.$set(this.localNode.config, 'reference_template_id', id)
      this.$nextTick(() => this.pullWaitButtons())
    },
    removeWaitButton (i) {
      this.localNode.config.buttons.splice(i, 1)
      this.emitUpdate()
    },
    onFieldChange () {
      this.localNode.config.field_value = ''
      if (this.selectedFieldType) {
        this.localNode.config.field_name = this.selectedFieldType.label || ''
      }
      this.emitUpdate()
    },
    onUpdateStatusChange () {
      this.localNode.config.ref_status = null
      this.emitUpdate()
    },
    onStatusChangedTriggerChange () {
      this.localNode.config.ref_status = null
      this.emitUpdate()
    },
    onConditionFieldChange () {
      this.localNode.config.value = ''
      this.localNode.config.node_ref_id = null
      this.localNode.config.ref_status = null
      const ops = this.conditionOperators
      if (ops.length > 0) {
        this.localNode.config.operator = ops[0].value
      }
      this.emitUpdate()
    },
    onConditionOperatorChange () {
      if (['is_empty', 'is_not_empty'].includes(this.localNode.config.operator)) {
        this.localNode.config.value = ''
      }
      this.emitUpdate()
    },
    getStepsByKind (kind) {
      return this.nodes.filter(n => n.kind === kind)
    },
    insertMergeTag (tag) {
      const el = this.$el.querySelector('textarea')
      if (!el) {
        this.localNode.config.note_text = (this.localNode.config.note_text || '') + tag
        this.emitUpdate()
        return
      }
      const start = el.selectionStart
      const end = el.selectionEnd
      const text = this.localNode.config.note_text || ''
      this.localNode.config.note_text = text.substring(0, start) + tag + text.substring(end)
      this.emitUpdate()
      this.$nextTick(() => {
        el.focus()
        el.setSelectionRange(start + tag.length, start + tag.length)
      })
    },
    openEmailPreview () {
      const template = this.selectedEmailTemplate
      if (!template) return
      this.previewType = 'email'
      this.previewContent = this.formatEmailPreview(template)
      this.showPreview = true
    },
    openWaPreview () {
      const template = this.selectedWaTemplate
      if (!template) return
      this.previewType = 'whatsapp'
      this.previewContent = this.formatWaPreview(template)
      this.showPreview = true
    },
    formatEmailPreview (template) {
      let content = template.content || template.body || ''
      content = content.replace(/\{\$nome\}/gi, 'Nome do participante')
      content = content.replace(/\{\$email\}/gi, 'email@exemplo.com')
      content = content.replace(/\{\$evento\}/gi, 'Nome do Evento')
      return content
    },
    formatWaPreview (template) {
      let body = ''
      if (template.components) {
        const bodyComp = template.components.find(c => c.type === 'BODY')
        body = bodyComp ? bodyComp.text : ''
      } else {
        body = template.body || template.content || ''
      }
      body = body.replace(/\{\{1\}\}/g, 'Nome do participante')
      body = body.replace(/\{\{2\}\}/g, 'Nome do Evento')
      body = body.replace(/\{\{(\d+)\}\}/g, '(variavel $1)')
      return body
    }
  }
}
</script>

<style scoped>
.node-editor {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Editor Header - matches FieldEditor */
.editor-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.editor-header-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.editor-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.editor-title-info {
  flex: 1;
  min-width: 0;
}

.label-input {
  display: block;
  width: 100%;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  padding: 2px 4px;
  border-radius: 4px;
  background: transparent;
}
.label-input:focus {
  outline: none;
  background: #f8f9fa;
  box-shadow: 0 0 0 2px rgba(85, 110, 230, 0.1);
}

.btn-close-editor {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-size: 20px;
  color: #9ca3af;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
}
.btn-close-editor:hover {
  color: #374151;
  background: #f3f4f6;
}

/* Editor Content - scroll on hover */
.editor-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}
.editor-content:hover {
  scrollbar-color: #d0d5dd transparent;
}
.editor-content::-webkit-scrollbar { width: 5px; }
.editor-content::-webkit-scrollbar-track { background: transparent; }
.editor-content::-webkit-scrollbar-thumb { background-color: transparent; border-radius: 3px; }
.editor-content:hover::-webkit-scrollbar-thumb { background-color: #d0d5dd; }
.editor-content::-webkit-scrollbar-thumb:hover { background-color: #b0b5bd; }

/* Settings Sections - matches FieldEditor */
.settings-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f3f4f6;
}
.settings-section:last-child { border-bottom: none; }

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
}

/* Form Groups - matches FieldEditor */
.form-group { margin-bottom: 16px; }
.form-group:last-child { margin-bottom: 0; }

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-group .form-control {
  font-size: 13px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.form-group .form-control:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

.form-group small,
.help-text {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #9ca3af;
}

/* Toggle group - matches FieldEditor */
.toggle-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.toggle-group label { margin-bottom: 0; }

/* Inline fields (e.g. number + select) */
.inline-fields {
  display: flex;
  gap: 8px;
}

/* Editor Footer */
.editor-footer {
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  text-align: center;
}

.btn-remove-node {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  font-size: 13px;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-remove-node:hover {
  background: #fee2e2;
  border-color: #fecaca;
}

/* Merge tags for Internal Note */
.merge-tags-section {
  margin-top: 12px;
}
.merge-tags-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 8px;
}
.merge-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.merge-tag {
  display: inline-block;
  padding: 3px 8px;
  background: #f0f4ff;
  border: 1px solid #e0e7ff;
  border-radius: 4px;
  font-size: 12px;
  font-family: monospace;
  color: #556ee6;
  cursor: pointer;
  transition: all 0.15s;
}
.merge-tag:hover {
  background: #e0e7ff;
  border-color: #556ee6;
}

/* Note destination info */
.note-destination {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dest-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.dest-item > i {
  font-size: 18px;
  color: #556ee6;
  margin-top: 1px;
  flex-shrink: 0;
}
.dest-title {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}
.dest-desc {
  display: block;
  font-size: 11px;
  color: #9ca3af;
  margin-top: 1px;
}

/* Retry section */
.retry-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
}

/* Condition summary */
.condition-summary {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  padding: 10px 12px;
  background: #f0f4ff;
  border: 1px solid #e0e7ff;
  border-radius: 8px;
  font-size: 12px;
  color: #374151;
  line-height: 1.5;
}
.condition-summary i {
  color: #556ee6;
  font-size: 16px;
  flex-shrink: 0;
}

/* Preview button */
.btn-preview {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  color: #556ee6;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 8px;
}
.btn-preview:hover {
  border-color: #556ee6;
  background: #f0f4ff;
}

/* Preview modal styles */
.preview-email {
  padding: 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  max-height: 500px;
  overflow-y: auto;
}
.preview-whatsapp {
  padding: 24px;
  background: #e5ddd5;
  border-radius: 8px;
  min-height: 200px;
  display: flex;
  align-items: flex-start;
}
.wa-bubble {
  background: #dcf8c6;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
  color: #303030;
  max-width: 80%;
  white-space: pre-wrap;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
</style>

<style>
.node-editor .multiselect,
.node-editor .multiselect__input,
.node-editor .multiselect__single,
.node-editor .multiselect__placeholder,
.node-editor .multiselect__option,
.node-editor .multiselect__tags {
  font-size: 13px;
}
.node-editor .multiselect__tags {
  min-height: 36px;
  border-color: #e9ecef;
}
.node-editor .multiselect__tag {
  font-size: 12px;
  background: #556ee6;
}
.node-editor .multiselect__tag-icon:after {
  color: #fff;
}
.node-editor .multiselect__tag-icon:hover {
  background: #3b52c4;
}
</style>
